/**
 * Web-safe haptics wrapper.
 * On native platforms, dynamically imports expo-haptics.
 * On web, provides no-op stubs to prevent navigator.vibrate errors.
 * ZERO static imports from expo-haptics — the bundler must not include it in web builds.
 */
import { Platform } from "react-native";

const isWeb = Platform.OS === "web";

// Local enum copies so we don't statically import expo-haptics
export enum ImpactFeedbackStyle {
  Light = "light",
  Medium = "medium",
  Heavy = "heavy",
}

export enum NotificationFeedbackType {
  Success = "success",
  Warning = "warning",
  Error = "error",
}

// Web fallback: Vibration API. Chrome blocks navigator.vibrate until the
// user has interacted with the page ("[Intervention] Blocked call…" console
// spam on every programmatic haptic during load) — so we only vibrate after
// the first real pointer/key interaction.
let userHasInteracted = false;
if (isWeb && typeof document !== "undefined") {
  const mark = () => {
    userHasInteracted = true;
    document.removeEventListener("pointerdown", mark);
    document.removeEventListener("keydown", mark);
  };
  document.addEventListener("pointerdown", mark, { once: true });
  document.addEventListener("keydown", mark, { once: true });
}

function webVibrate(pattern: number | number[]): void {
  try {
    if (!userHasInteracted) return;
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate(pattern);
    }
  } catch {
    /* vibration is best-effort */
  }
}

export async function impactAsync(
  style?: ImpactFeedbackStyle
): Promise<void> {
  if (isWeb) {
    webVibrate(style === ImpactFeedbackStyle.Heavy ? 30 : style === ImpactFeedbackStyle.Medium ? 20 : 10);
    return;
  }
  const haptics = await import("expo-haptics");
  return haptics.impactAsync(style as any);
}

export async function notificationAsync(
  type?: NotificationFeedbackType
): Promise<void> {
  if (isWeb) {
    webVibrate(
      type === NotificationFeedbackType.Error
        ? [40, 60, 40]
        : type === NotificationFeedbackType.Warning
          ? [30, 40, 30]
          : [15, 30, 15],
    );
    return;
  }
  const haptics = await import("expo-haptics");
  return haptics.notificationAsync(type as any);
}

export async function selectionAsync(): Promise<void> {
  if (isWeb) {
    webVibrate(8);
    return;
  }
  const haptics = await import("expo-haptics");
  return haptics.selectionAsync();
}
